import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, DollarSign } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import { BulkPayDialog } from './BulkPayDialog';
import { StripeCheckoutDialog } from './StripeCheckoutDialog';
import {
  useInitiateManualCharges,
  useConfirmPayment,
  type InitiateManualChargesResponse,
} from '@/api/extensions/usePaymentOps';
import { useToast } from '@/contexts/ToastContext';

export interface OrgOutstandingBalanceUnpaidEvent {
  /**
   * Optional only because legacy 402 responses (pre per-org gate) didn't
   * include it. New backend always sends it; absence disables bulk-pay.
   */
  registration_id?: string;
  event_id: string;
  event_name: string | null;
  amount_owed: number;
}

interface OrgOutstandingBalanceDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Name of the organization the user owes. Falls back to a generic
   * label when the backend couldn't resolve it (rare — would mean a
   * rodeo without a primary org).
   */
  organizationName: string | null;
  unpaidEvents: OrgOutstandingBalanceUnpaidEvent[];
  /**
   * Org-scoped manual-charge debt that also has to clear before the
   * user can register for events with this org. Optional so older
   * callers omit it cleanly. Net owed and adjustment ids drive the
   * bulk-pay basket alongside the registration ids.
   */
  manualCharges?: {
    net_owed: number;
    adjustment_ids: string[];
    /** Per-charge breakdown (reason + amount) so the dialog shows WHY each
     *  blocking fee is owed instead of a generic "committee charges" line. */
    charges?: Array<{ id: string; amount: number; reason: string | null }>;
  };
  /**
   * Fired after the bulk-pay flow successfully captures. The parent
   * uses this to close itself + invalidate registration / balance
   * queries so the user can immediately retry registration.
   */
  onPaid?: () => void;
}

/**
 * Surfaced when the user attempts to register for an event whose owning
 * organization they have past-due unpaid fees with. Lists the specific
 * unpaid registrations (event name + dollars) and offers a single
 * "Pay outstanding balance" CTA that mints ONE Stripe PaymentIntent
 * covering just THIS org's debts via the shared bulk-pay flow —
 * cross-org balances are intentionally NOT pulled in.
 */
export function OrgOutstandingBalanceDialog({
  open,
  onClose,
  organizationName,
  unpaidEvents,
  manualCharges,
  onPaid,
}: OrgOutstandingBalanceDialogProps) {
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [manualOnlyOpen, setManualOnlyOpen] = useState(false);
  const initiateManual = useInitiateManualCharges();
  const confirmManual = useConfirmPayment();
  const { showSuccess } = useToast();

  const eventsTotal = useMemo(
    () =>
      unpaidEvents.reduce(
        (sum, e) => sum + (Number(e.amount_owed) || 0),
        0,
      ),
    [unpaidEvents],
  );
  const manualOwed = manualCharges?.net_owed ?? 0;
  const total = eventsTotal + manualOwed;

  const registrationIds = useMemo(
    () =>
      unpaidEvents
        .map((e) => e.registration_id)
        .filter((id): id is string => !!id),
    [unpaidEvents],
  );

  const manualAdjustmentIds = manualCharges?.adjustment_ids ?? [];
  const hasManualOnly =
    registrationIds.length === 0 && manualAdjustmentIds.length > 0;

  // Pay button is enabled whenever there's SOMETHING to pay — either
  // event-registration debt (bulk path) or manual-charge debt (org-
  // scoped manual-only path). The two paths mint different
  // PaymentIntents under the hood; the dialog routes between them
  // based on what's outstanding.
  const canPayInline =
    registrationIds.length > 0 || manualAdjustmentIds.length > 0;

  // Manual-only Stripe checkout: mints a PI scoped to JUST these
  // org-tied adjustment ids (not every unsettled charge across all the
  // user's orgs — that would over-collect).
  //
  // Manual-charge flow is still eager-mint (the deferred-mint refactor
  // landed for event registrations only). The dialog now expects a
  // quote-then-confirm shape — we stash the eager-mint result in a ref
  // and replay it via confirmIntent so the dialog flow works uniformly.
  // The trade-off: opening this dialog and bailing still leaves an
  // orphan PI in Stripe. Migrating /payment-ops/initiate-manual-charges
  // to the deferred pattern is a follow-up; it shares the same
  // eager-mint problem but isn't part of the event-entry bug.
  const eagerManualOrderRef = useRef<InitiateManualChargesResponse | null>(
    null,
  );

  const quoteManualOnly = useCallback(async () => {
    const res = await initiateManual.mutateAsync({
      adjustment_ids: manualAdjustmentIds,
    });
    eagerManualOrderRef.current = res;
    return {
      payment_id: res.payment_id,
      stripe_publishable_key: res.stripe_publishable_key ?? undefined,
      total_amount: res.amount,
    };
  }, [initiateManual, manualAdjustmentIds]);

  const confirmManualIntent = useCallback(
    async (_paymentId: string, _method: 'card' | 'us_bank_account') => {
      const stashed = eagerManualOrderRef.current;
      if (!stashed) {
        throw new Error('Manual-charge order is not loaded yet');
      }
      return {
        client_secret: stashed.client_secret,
        stripe_payment_intent_id: stashed.stripe_payment_intent_id,
        total_amount: stashed.amount,
        transaction_fee_total: 0,
        stripe_publishable_key: stashed.stripe_publishable_key ?? '',
      };
    },
    [],
  );

  // Manual-charge PaymentIntents are not bound to event registrations
  // (they pay off account_adjustments), so there's no event transaction
  // fee to skip on ACH selection — we just need to settle the captured
  // amount. The dialog now passes a method-info second arg; we accept
  // and ignore it for this path.
  const onApproveManualOnly = useCallback(
    async (paymentIntentId: string) => {
      await confirmManual.mutateAsync({ payment_intent_id: paymentIntentId });
    },
    [confirmManual],
  );

  const handlePayClick = () => {
    if (hasManualOnly) {
      setManualOnlyOpen(true);
    } else {
      setBulkPayOpen(true);
    }
  };

  const orgLabel = organizationName?.trim() || 'this organization';

  return (
    <>
      <Dialog open={open && !bulkPayOpen} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Outstanding balance with {orgLabel}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              You can&apos;t enter events in this rodeo until you
              settle your balance with {orgLabel}. The fees below are for
              past events you haven&apos;t paid for yet. Balances with
              other organizations are not affected — you can still
              enter their rodeos.
            </p>
            {canPayInline && unpaidEvents.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Didn&apos;t mean to start one of these entries? Open it
                from your Rodeos page and cancel it from there.
              </p>
            )}

            {unpaidEvents.length > 0 || manualOwed > 0 ? (
              <div className="rounded-lg border divide-y">
                {unpaidEvents.map((ue) => (
                  <div
                    key={`${ue.registration_id ?? ue.event_id}`}
                    className="flex items-start justify-between gap-3 p-3 text-sm"
                  >
                    <span className="text-foreground">
                      {ue.event_name ?? 'Unknown event'}
                    </span>
                    <span className="font-medium tabular-nums">
                      {formatCurrency(ue.amount_owed, 2)}
                    </span>
                  </div>
                ))}
                {/* Per-charge breakdown when we have it (shows the admin's
                    entered reason, e.g. "fine"); falls back to a single
                    aggregate line for older payloads. */}
                {manualOwed > 0 &&
                  (manualCharges?.charges && manualCharges.charges.length > 0 ? (
                    manualCharges.charges.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-start justify-between gap-3 p-3 text-sm"
                      >
                        <span className="text-foreground">
                          {c.reason?.trim() || 'Committee charge'}
                          <span className="block text-[11px] text-muted-foreground">
                            Admin-recorded fee with {orgLabel}
                          </span>
                        </span>
                        <span className="font-medium tabular-nums">
                          {formatCurrency(c.amount, 2)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-start justify-between gap-3 p-3 text-sm">
                      <span className="text-foreground">
                        Committee charges
                        <span className="block text-[11px] text-muted-foreground">
                          Admin-recorded fees on your account with {orgLabel}
                        </span>
                      </span>
                      <span className="font-medium tabular-nums">
                        {formatCurrency(manualOwed, 2)}
                      </span>
                    </div>
                  ))}
                <div className="flex items-center justify-between gap-3 p-3 text-sm bg-muted/40">
                  <span className="font-medium">Total due</span>
                  <span className="font-semibold tabular-nums">
                    {formatCurrency(total, 2)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
                We couldn&apos;t load the specific past-due fees, but the
                server reported a balance with {orgLabel}. Please open
                the Funds page to settle it.
              </div>
            )}

            {!canPayInline && unpaidEvents.length > 0 && (
              <p className="text-xs text-muted-foreground">
                These fees aren&apos;t payable from this dialog because
                their entry ids weren&apos;t returned. Open the
                Funds page to settle them.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button disabled={!canPayInline} onClick={handlePayClick}>
              <DollarSign className="h-4 w-4 mr-1" />
              Pay outstanding balance
              {total > 0 ? ` (${formatCurrency(total, 2)})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {registrationIds.length > 0 && (
        <BulkPayDialog
          open={bulkPayOpen}
          onClose={() => setBulkPayOpen(false)}
          registrationIds={registrationIds}
          manualChargeAdjustmentIds={
            manualAdjustmentIds.length > 0 ? manualAdjustmentIds : undefined
          }
          fallbackTotal={total}
          onSuccess={() => {
            setBulkPayOpen(false);
            onPaid?.();
            onClose();
          }}
        />
      )}

      {hasManualOnly && (
        <StripeCheckoutDialog
          open={manualOnlyOpen}
          onClose={() => setManualOnlyOpen(false)}
          title={`Pay outstanding charges${
            organizationName ? ` — ${organizationName}` : ''
          }`}
          amountLabel="Amount due"
          fallbackTotal={total}
          triggerKey={manualAdjustmentIds.join(',')}
          quoteOrder={quoteManualOnly}
          confirmIntent={confirmManualIntent}
          onApprove={onApproveManualOnly}
          onApproved={() => {
            setManualOnlyOpen(false);
            onPaid?.();
            onClose();
            showSuccess(
              'Payment successful',
              'Your committee charges are settled — you can register now.',
            );
          }}
        />
      )}
    </>
  );
}
